import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useState } from 'react';

import { advertisersApi, queryKeys } from '../../lib/api/resources';
import type { Advertiser, AdvertiserStatus } from '../../lib/api/types';
import { count } from '../../lib/format';
import { useToast } from '../../app/toast';
import {
  AdvertiserStatusBadge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Table,
  TableSkeleton,
  Td,
  Th,
} from '../../components/ui';

/**
 * The businesses buying campaigns.
 *
 * <p>Advertisers are deactivated, never deleted: their campaigns carry billing history that has to
 * stay explainable after the relationship ends.
 */
export function AdvertiserListPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const params = { search: search || undefined, page, size: 20 };
  const advertisers = useQuery({
    queryKey: queryKeys.advertisers(params),
    queryFn: () => advertisersApi.list(params),
  });

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-headline-lg text-text-primary">Anunciantes</h1>
        <div className="flex items-center gap-3">
          <Input
            aria-label="Buscar anunciante"
            placeholder="Buscar por nombre"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            className="w-64"
          />
          <Link to="/advertisers/new">
            <Button variant="primary">Nuevo anunciante</Button>
          </Link>
        </div>
      </header>

      <Card className="p-0">
        {advertisers.isLoading ? (
          <div className="p-6">
            <TableSkeleton rows={5} columns={5} />
          </div>
        ) : advertisers.isError ? (
          <div className="p-6">
            <ErrorState
              message={(advertisers.error as Error).message}
              onRetry={() => void advertisers.refetch()}
            />
          </div>
        ) : advertisers.data!.content.length === 0 ? (
          <div className="p-6">
            <EmptyState
              title={search ? 'Sin resultados' : 'Todavía no hay anunciantes'}
              description={
                search
                  ? 'Prueba con otro nombre.'
                  : 'Registra al primer anunciante para poder crearle campañas.'
              }
              action={
                !search && (
                  <Link to="/advertisers/new">
                    <Button variant="primary">Nuevo anunciante</Button>
                  </Link>
                )
              }
            />
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Anunciante</Th>
                <Th>Contacto</Th>
                <Th>Estado</Th>
                <Th numeric>Campañas</Th>
                <Th>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {advertisers.data!.content.map((advertiser) => (
                <AdvertiserRow key={advertiser.id} advertiser={advertiser} />
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function AdvertiserRow({ advertiser }: { advertiser: Advertiser }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const changeStatus = useMutation({
    mutationFn: (status: AdvertiserStatus) => advertisersApi.changeStatus(advertiser.id, status),
    onSuccess: (result) => {
      // Deactivation pauses running campaigns. Saying how many is the difference between an
      // operator knowing what they did and discovering it when an advertiser calls.
      toast.success(
        result.pausedCampaigns > 0
          ? `Anunciante desactivado. Se pausaron ${result.pausedCampaigns} campaña(s).`
          : result.status === 'ACTIVE'
            ? 'Anunciante activado'
            : 'Anunciante desactivado',
      );
      void queryClient.invalidateQueries({ queryKey: ['advertisers'] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: () => advertisersApi.remove(advertiser.id),
    onSuccess: () => {
      toast.success('Anunciante eliminado');
      void queryClient.invalidateQueries({ queryKey: ['advertisers'] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    // The server's message explains which campaign has history and what to do instead. Replacing
    // it with something generic would leave the operator with a refusal and no next step.
    onError: (error: Error) => toast.error(error.message),
  });

  const deactivating = advertiser.status === 'ACTIVE';

  return (
    <tr className="hover:bg-surface-tertiary/40">
      <Td>
        <p className="text-text-primary">{advertiser.name}</p>
        <p className="text-label-md text-text-secondary">{advertiser.industry ?? '—'}</p>
      </Td>
      <Td>
        <p className="text-body-md">{advertiser.contactEmail ?? '—'}</p>
        <p className="font-mono text-label-md text-text-secondary">
          {advertiser.contactPhone ?? ''}
        </p>
      </Td>
      <Td>
        <AdvertiserStatusBadge status={advertiser.status} />
      </Td>
      <Td numeric>{count(advertiser.campaignCount)}</Td>
      <Td>
        <div className="flex gap-2">
          <Link to={`/advertisers/${advertiser.id}/edit`}>
            <Button className="px-2 py-1 text-label-md">Editar</Button>
          </Link>
          <Button
            variant={deactivating ? 'destructive' : 'secondary'}
            loading={changeStatus.isPending}
            className="px-2 py-1 text-label-md"
            onClick={() => {
              if (deactivating) {
                const confirmed = window.confirm(
                  `Desactivar «${advertiser.name}»? Sus campañas activas se pausarán y dejarán de servir.`,
                );
                if (!confirmed) {
                  return;
                }
              }
              changeStatus.mutate(deactivating ? 'INACTIVE' : 'ACTIVE');
            }}
          >
            {deactivating ? 'Desactivar' : 'Activar'}
          </Button>
          <Button
            variant="ghost"
            loading={remove.isPending}
            className="px-2 py-1 text-label-md"
            onClick={() => {
              // Spelled out because it cascades. An operator who reads "eliminar" and expects
              // only the advertiser to go would be surprised by the campaigns.
              const confirmed = window.confirm(
                advertiser.campaignCount > 0
                  ? `Eliminar «${advertiser.name}» y sus ${advertiser.campaignCount} campaña(s)? ` +
                      'No se puede deshacer. Si alguna ya tiene eventos registrados, no se ' +
                      'eliminará nada.'
                  : `Eliminar «${advertiser.name}»? No se puede deshacer.`,
              );
              if (confirmed) {
                remove.mutate();
              }
            }}
          >
            Eliminar
          </Button>
        </div>
      </Td>
    </tr>
  );
}
